/**
 * Ler um pacote de plugin web: o `.html` único (com o manifesto dentro) ou o `.zip` com
 * `manifest.json` na raiz. Um `.zip` com `module.xml` e sem manifesto é um `.rpk` (SDK3), formato que
 * o RPG Desk não suporta: vira um erro que diz isso.
 *
 * O resultado traz o `.zip` que vai para o servidor: o arquivo único vira um `.zip` com
 * `manifest.json` e `index.html`, e o servidor só lida com `.zip`.
 */
import { strToU8, unzipSync, zipSync } from 'fflate';
import { isHtmlPath } from './constants.js';
import { hasErrors, lineAt, type Issue } from './issues.js';
import { SINGLE_ENTRY, parseManifest, type WebManifest } from './manifest.js';
import { MESSAGES, type Lang } from './messages.js';
import { decodeText, verifyFiles } from './verify.js';

export type PackageFormat = 'web-zip' | 'web-html' | 'unknown';

export type WebPackage = {
  format: 'web-zip' | 'web-html';
  manifest: WebManifest;
  /** caminho → bytes, como no .zip */
  files: Map<string, Uint8Array>;
  /** o .zip a enviar */
  zip: Uint8Array;
};

export type Inspection = {
  format: PackageFormat;
  /** o pacote pronto, quando não há erro */
  pkg: WebPackage | null;
  /** o manifesto validado, mesmo com erro nos arquivos (para a tela dizer de qual plugin se trata) */
  manifest: WebManifest | null;
  issues: Issue[];
};

/** Tamanho somado dos arquivos descompactados: um .zip pequeno não pode virar gigabytes. */
export const MAX_UNPACKED_BYTES = 64 * 1024 * 1024;

/** Descompacta conferindo antes o tamanho declarado de cada arquivo (bomba de zip). */
function safeUnzip(bytes: Uint8Array): Record<string, Uint8Array> {
  let total = 0;
  let count = 0;
  return unzipSync(bytes, {
    filter: (file) => {
      total += file.originalSize;
      count += 1;
      if (total > MAX_UNPACKED_BYTES || count > 2000) throw new Error('o .zip descompactado passa do limite');
      return true;
    },
  });
}

/** Lixo que os sistemas põem nos .zip e que não faz parte do plugin. */
const JUNK = /(^|\/)(__MACOSX\/|\.DS_Store$|Thumbs\.db$|desktop\.ini$)/i;

const MANIFEST_SCRIPT = /<script\b[^>]*\btype\s*=\s*["']application\/rpgdesk\+json["'][^>]*>([\s\S]*?)<\/script\s*>/i;

/** O manifesto embutido num arquivo único, ou null. */
export function manifestFromHtml(html: string): { json: string; index: number } | null {
  const m = MANIFEST_SCRIPT.exec(html);
  if (!m) return null;
  return { json: m[1]!, index: (m.index ?? 0) + m[0].indexOf('>') + 1 };
}

function looksLikeHtml(name: string, bytes: Uint8Array): boolean {
  if (isHtmlPath(name.toLowerCase())) return true;
  const head = decodeText(bytes.subarray(0, 256)).trimStart();
  return head.startsWith('<');
}

/** Começa com a assinatura de um .zip (`PK`). */
export const isZip = (bytes: Uint8Array) => bytes.length >= 4 && bytes[0] === 0x50 && bytes[1] === 0x4b;

function parseJson(text: string, file: string, lang: Lang, offsetText?: { whole: string; index: number }): { value: unknown; issue: Issue | null } {
  try {
    return { value: JSON.parse(text), issue: null };
  } catch (error) {
    const detail = error instanceof Error ? error.message : String(error);
    const at = /position (\d+)/.exec(detail);
    const line = offsetText && at ? lineAt(offsetText.whole, offsetText.index + Number(at[1])) : undefined;
    return {
      value: undefined,
      issue: { level: 'error', code: 'manifest-json', message: MESSAGES[lang].package.badJson(detail), fix: MESSAGES[lang].package.badJsonFix, file, ...(line ? { line } : {}) },
    };
  }
}

function inspectHtml(name: string, bytes: Uint8Array, lang: Lang): Inspection {
  const html = decodeText(bytes);
  const found = manifestFromHtml(html);
  if (!found) {
    const M = MESSAGES[lang].package;
    return { format: 'web-html', pkg: null, manifest: null, issues: [{ level: 'error', code: 'manifest-missing', message: M.htmlNoManifest, fix: M.htmlNoManifestFix, file: name }] };
  }
  const { value, issue } = parseJson(found.json, name, lang, { whole: html, index: found.index });
  if (issue) return { format: 'web-html', pkg: null, manifest: null, issues: [issue] };
  const { manifest, issues } = parseManifest(value, { single: true, file: name, lang });
  if (!manifest) return { format: 'web-html', pkg: null, manifest: null, issues };
  const files = new Map<string, Uint8Array>([[SINGLE_ENTRY, bytes]]);
  // o relatório cita o nome que a pessoa conhece, não o index.html do .zip montado
  const fileIssues = verifyFiles(manifest, files, { packageBytes: bytes.length, lang }).map((i) => (i.file === SINGLE_ENTRY ? { ...i, file: name } : i));
  const all = [...issues, ...fileIssues];
  if (hasErrors(all)) return { format: 'web-html', pkg: null, manifest, issues: all };
  const zipped = zipSync({ 'manifest.json': strToU8(JSON.stringify(withEntries(manifest), null, 2)), [SINGLE_ENTRY]: bytes });
  return { format: 'web-html', pkg: { format: 'web-html', manifest, files, zip: zipped }, manifest, issues: all };
}

/** O manifesto do arquivo único com a entrada explícita, como fica no .zip montado. */
function withEntries(manifest: WebManifest): WebManifest {
  return { ...manifest, sheets: manifest.sheets.map((d) => ({ ...d, entry: SINGLE_ENTRY })), tables: manifest.tables.map((d) => ({ ...d, entry: SINGLE_ENTRY })) };
}

/** Por que um .zip não pôde ser lido: não é zip, ou descompactado passa do limite (bomba). */
export type ZipUnpackError = 'not-zip' | 'too-big';

/** O erro de leitura do .zip como um relatório de inspeção, na língua pedida. */
export function zipErrorInspection(name: string, kind: ZipUnpackError, lang: Lang): Inspection {
  const M = MESSAGES[lang].package;
  return {
    format: 'unknown',
    pkg: null,
    manifest: null,
    issues: [
      kind === 'too-big'
        ? { level: 'error', code: 'package-unpacked', message: M.unpacked(MAX_UNPACKED_BYTES / 1024 / 1024), fix: M.unpackedFix, file: name }
        : { level: 'error', code: 'not-zip', message: M.notZip, fix: M.notZipFix, file: name },
    ],
  };
}

/** Tira do .zip descompactado as pastas e o lixo dos sistemas. */
export function cleanZipEntries(entries: Record<string, Uint8Array>): Map<string, Uint8Array> {
  const out = new Map<string, Uint8Array>();
  for (const [path, data] of Object.entries(entries)) if (!path.endsWith('/') && !JUNK.test(path)) out.set(path, data);
  return out;
}

/** A pasta de cima única que guarda o `manifest.json` (compactaram a pasta em vez do conteúdo dela), ou ''. */
function wrappingFolder(files: ReadonlyMap<string, Uint8Array>): string {
  if (files.has('manifest.json')) return '';
  const tops = new Set([...files.keys()].map((p) => (p.includes('/') ? p.slice(0, p.indexOf('/') + 1) : '')));
  const top = tops.size === 1 ? [...tops][0]! : '';
  return top && files.has(`${top}manifest.json`) ? top : '';
}

/**
 * Os arquivos de um .zip de plugin como o RPG Desk os serve: sem as pastas, sem o lixo dos sistemas
 * e sem a pasta de cima que a conferência aceita com um aviso (`inspectZipEntries`). O servidor
 * desempacota com isto, então um .zip instalado direto do Desk Cloud abre como foi conferido.
 */
export function packageFiles(entries: Record<string, Uint8Array>): Map<string, Uint8Array> {
  const files = cleanZipEntries(entries);
  const top = wrappingFolder(files);
  return top ? new Map([...files].map(([p, d]) => [p.slice(top.length), d])) : files;
}

function inspectZip(name: string, bytes: Uint8Array, lang: Lang): Inspection {
  let entries: Record<string, Uint8Array>;
  try {
    entries = safeUnzip(bytes);
  } catch (error) {
    return zipErrorInspection(name, error instanceof Error && error.message.includes('limite') ? 'too-big' : 'not-zip', lang);
  }
  return inspectZipEntries(name, entries, bytes, lang);
}

/**
 * Confere um .zip já descompactado (`entries`: caminho → bytes). `originalZip` é o .zip de onde ele
 * veio, reaproveitado quando nada precisou mudar. Separado de `inspectZip` para o servidor
 * descompactar fora do event loop (apps/server/src/unpackZip.ts) e só então conferir.
 */
export function inspectZipEntries(name: string, entries: Record<string, Uint8Array>, originalZip: Uint8Array, lang: Lang): Inspection {
  const M = MESSAGES[lang].package;
  const bytes = originalZip;
  const issues: Issue[] = [];
  let files = new Map<string, Uint8Array>();
  let changed = false;
  for (const [path, data] of Object.entries(entries)) {
    if (path.endsWith('/')) continue;
    if (JUNK.test(path)) {
      changed = true;
      continue;
    }
    files.set(path, data);
  }
  const lower = new Map([...files.keys()].map((p) => [p.toLowerCase(), p]));
  if (!files.has('manifest.json') && lower.has('module.xml'))
    return { format: 'unknown', pkg: null, manifest: null, issues: [{ level: 'error', code: 'rpk-unsupported', message: M.rpk, fix: M.rpkFix, file: name }] };
  if (!files.has('manifest.json')) {
    // o erro mais comum: compactar a pasta em vez do conteúdo dela
    const top = wrappingFolder(files);
    if (top) {
      files = new Map([...files].map(([p, d]) => [p.slice(top.length), d]));
      changed = true;
      issues.push({ level: 'warning', code: 'zip-folder', message: M.zipFolder(top.slice(0, -1)), fix: M.zipFolderFix, file: name });
    } else {
      return { format: 'unknown', pkg: null, manifest: null, issues: [{ level: 'error', code: 'manifest-missing', message: M.zipNoManifest, fix: M.zipNoManifestFix, file: name }] };
    }
  }
  const { value, issue } = parseJson(decodeText(files.get('manifest.json')!), 'manifest.json', lang);
  if (issue) return { format: 'web-zip', pkg: null, manifest: null, issues: [...issues, issue] };
  const parsed = parseManifest(value, { single: false, file: 'manifest.json', lang });
  issues.push(...parsed.issues);
  if (!parsed.manifest) return { format: 'web-zip', pkg: null, manifest: null, issues };
  issues.push(...verifyFiles(parsed.manifest, files, { packageBytes: bytes.length, lang }));
  if (hasErrors(issues)) return { format: 'web-zip', pkg: null, manifest: parsed.manifest, issues };
  const zip = changed ? zipSync(Object.fromEntries(files)) : bytes;
  return { format: 'web-zip', pkg: { format: 'web-zip', manifest: parsed.manifest, files, zip }, manifest: parsed.manifest, issues };
}

/**
 * Lê e confere um pacote (o nome serve para reconhecer o .html e para o relatório). `lang`: a língua
 * dos textos dos problemas (os códigos são os mesmos).
 */
export function inspectPackage(name: string, bytes: Uint8Array, opts: { lang?: Lang } = {}): Inspection {
  const lang = opts.lang ?? 'pt';
  if (isZip(bytes)) return inspectZip(name, bytes, lang);
  if (looksLikeHtml(name, bytes)) return inspectHtml(name, bytes, lang);
  return { format: 'unknown', pkg: null, manifest: null, issues: [{ level: 'error', code: 'not-plugin', message: MESSAGES[lang].package.notPlugin, file: name }] };
}

/** Os arquivos de um .zip já aceito (síncrono: o navegador e o comando de linha). */
export function unpackZip(bytes: Uint8Array): Map<string, Uint8Array> {
  return packageFiles(safeUnzip(bytes));
}
